const CONSENT_VERSION = "2026-08-06";
const CONSENT_STORAGE_KEY = "payeplast_consent_record";
const CONSENT_PENDING_KEY = "payeplast_consent_pending";
const CONSENT_ANON_ID_KEY = "payeplast_consent_anon_id";

function safeParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function getAnonId() {
  const existingId = window.localStorage.getItem(CONSENT_ANON_ID_KEY);
  if (existingId) {
    return existingId;
  }

  const createdId =
    window.crypto?.randomUUID?.() || `anon-${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(CONSENT_ANON_ID_KEY, createdId);
  return createdId;
}

function bufferToHex(buffer) {
  return [...new Uint8Array(buffer)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function createSignature(payloadText) {
  if (window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(payloadText);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return bufferToHex(digest);
  }

  return btoa(unescape(encodeURIComponent(payloadText))).slice(0, 64);
}

export function getStoredConsent() {
  const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored ? safeParse(stored, null) : null;
}

export function hasAcceptedConsent(consentRecord) {
  if (!consentRecord) {
    return false;
  }

  return (
    consentRecord.status === "accepted" &&
    consentRecord.version === CONSENT_VERSION
  );
}

export function getConsentVersion() {
  return CONSENT_VERSION;
}

export async function buildAcceptedConsentPayload({ locale }) {
  const anonId = getAnonId();
  const acceptedAt = new Date().toISOString();
  const userAgent = window.navigator.userAgent || "unknown";
  const source = window.location.href;
  const signatureText = [
    anonId,
    CONSENT_VERSION,
    acceptedAt,
    locale || "tr",
    userAgent,
    source,
  ].join("|");

  const signature = await createSignature(signatureText);

  return {
    anonId,
    version: CONSENT_VERSION,
    status: "accepted",
    acceptedAt,
    locale: locale || "tr",
    userAgent,
    source,
    signature,
    documents: ["kvkPolicy", "cookiePolicy"],
  };
}

export function saveConsentRecord(record) {
  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
}

export function buildRejectedConsentRecord() {
  return {
    status: "rejected",
    version: CONSENT_VERSION,
    rejectedAt: new Date().toISOString(),
  };
}

function getPendingConsents() {
  const raw = window.localStorage.getItem(CONSENT_PENDING_KEY);
  return raw ? safeParse(raw, []) : [];
}

function setPendingConsents(records) {
  window.localStorage.setItem(CONSENT_PENDING_KEY, JSON.stringify(records));
}

function addPendingConsent(record) {
  const pending = getPendingConsents();
  pending.push(record);
  setPendingConsents(pending);
}

async function postConsentRecord(endpoint, record) {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(record),
  });

  if (!response.ok) {
    throw new Error(`Consent endpoint returned ${response.status}`);
  }
}

export async function submitConsentRecord(record) {
  const endpoint = process.env.REACT_APP_CONSENT_ENDPOINT || "/consent.php";
  if (!endpoint) {
    return;
  }

  try {
    await postConsentRecord(endpoint, record);
  } catch (error) {
    addPendingConsent(record);
  }
}

export async function flushPendingConsentRecords() {
  const endpoint = process.env.REACT_APP_CONSENT_ENDPOINT || "/consent.php";
  if (!endpoint) {
    return;
  }

  const pending = getPendingConsents();
  if (!pending.length) {
    return;
  }

  const stillPending = [];
  for (const record of pending) {
    try {
      // Try each record independently to avoid dropping unsent entries.
      // eslint-disable-next-line no-await-in-loop
      await postConsentRecord(endpoint, record);
    } catch (error) {
      stillPending.push(record);
    }
  }

  setPendingConsents(stillPending);
}
